import { useLazyQuery } from '@apollo/client';
import { Select, Spin } from 'antd';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DYNAMIC_CONFIG_TYPES,
  TAGS_CONFIG_SUBTYPES,
} from '../common/constants';
import { DYNAMIC_CONFIG_ITEMS } from '../modules/configs/modules/graphql/Queries';

const { Option } = Select;

const TAGS_LIMIT = 10;

const OrderTagsFilter = ({
  value = [],
  onChange,
  placeholder = 'Filter by Tags',
  className = '',
}) => {
  const [options, setOptions] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const skipRef = useRef(0);
  const searchRef = useRef('');
  // Each loadTags call gets a unique ID; stale responses are discarded on mismatch
  const requestIdRef = useRef(0);
  const emojiMapRef = useRef({});

  const [fetchTags] = useLazyQuery(DYNAMIC_CONFIG_ITEMS, {
    fetchPolicy: 'network-only',
  });

  const loadTags = useCallback(
    async (skip = 0, searchVal = '') => {
      const requestId = requestIdRef.current;
      try {
        const result = await fetchTags({
          variables: {
            filter: {
              configType: DYNAMIC_CONFIG_TYPES?.TAGS?.value,
              configSubtypes: [
                TAGS_CONFIG_SUBTYPES?.ORDERS?.value,
                TAGS_CONFIG_SUBTYPES?.USERS?.value,
              ],
              skip,
              limit: TAGS_LIMIT,
              search: searchVal || null,
              isActive: true,
            },
            sort: { field: 'createdAt', order: 'DESC' },
          },
        });

        // Discard if a newer request has since been dispatched
        if (requestId !== requestIdRef.current) return;

        const tags = result?.data?.dynamicConfigItems?.data || [];
        const total = result?.data?.dynamicConfigItems?.count || 0;
        const mapped = tags.map((tag) => ({
          label: tag.itemKey,
          emoji: tag.value || '',
          value: tag.id,
        }));
        mapped.forEach((o) => {
          emojiMapRef.current[o.value] = o.emoji;
        });

        if (skip === 0) {
          setOptions(mapped);
        } else {
          setOptions((prev) => {
            const existingIds = new Set(prev.map((o) => o.value));
            return [
              ...prev,
              ...mapped.filter((o) => !existingIds.has(o.value)),
            ];
          });
        }
        setHasMore(skip + tags.length < total);
      } catch {
        // network errors handled silently
      } finally {
        if (requestId === requestIdRef.current) {
          if (skip === 0) setIsInitialLoading(false);
          else setIsLoadingMore(false);
        }
      }
    },
    [fetchTags],
  );

  useEffect(() => {
    skipRef.current = 0;
    setOptions([]);
    setHasMore(true);
    setIsInitialLoading(true);
    loadTags(0, '');
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((val) => {
      skipRef.current = 0;
      searchRef.current = val;
      setOptions([]);
      setHasMore(true);
      setIsInitialLoading(true);
      loadTags(0, val);
    }, 500),
    [loadTags],
  );

  const handlePopupScroll = (e) => {
    const { target } = e;
    if (
      !isLoadingMore &&
      hasMore &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 20
    ) {
      const newSkip = skipRef.current + TAGS_LIMIT;
      skipRef.current = newSkip;
      setIsLoadingMore(true);
      loadTags(newSkip, searchRef.current);
    }
  };

  const tagRender = ({ value: tagValue, onClose, closable }) => {
    const emoji = emojiMapRef.current[tagValue];
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          background: 'rgba(0,0,0,0.06)',
          borderRadius: 4,
          padding: '4px',
          margin: '2px 2px 2px 0',
          fontSize: 13,
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        <span style={{ marginTop: '2px' }}>{emoji || '🏷️'}</span>
        {closable && (
          <span
            onClick={onClose}
            style={{
              marginLeft: 3,
              cursor: 'pointer',
              fontSize: 12,
              color: '#666',
              lineHeight: 1,
            }}
          >
            ×
          </span>
        )}
      </span>
    );
  };

  const dropdownRender = (menu) => (
    <>
      <style>{`.order-tags-dropdown .rc-virtual-list-holder { overflow-y: auto; scrollbar-width: none; } .order-tags-dropdown .rc-virtual-list-holder::-webkit-scrollbar { display: none; }`}</style>
      {menu}
      {isLoadingMore && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Spin size="small" />
        </div>
      )}
    </>
  );

  return (
    <Select
      mode="multiple"
      allowClear
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      loading={isInitialLoading && options.length === 0}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      dropdownRender={dropdownRender}
      filterOption={false}
      className={className}
      style={{ minWidth: 200 }}
      maxTagCount="responsive"
      tagRender={tagRender}
      popupClassName="order-tags-dropdown"
      notFoundContent={
        isInitialLoading ? <Spin size="small" /> : 'No tags found'
      }
    >
      {options.map((opt) => (
        <Option key={opt.value} value={opt.value}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {opt.emoji && (
              <span style={{ fontSize: 18, lineHeight: 1 }}>{opt.emoji}</span>
            )}
            <span>{opt.label}</span>
          </span>
        </Option>
      ))}
    </Select>
  );
};

export default OrderTagsFilter;
